/* eslint-disable @typescript-eslint/no-unused-vars */
export * from "ce/utils/permissionHelpers";
import {
  isPermitted,
  PERMISSION_TYPE as CE_PERMISSION_TYPE,
  LOGIC_FILTER,
} from "ce/utils/permissionHelpers";

export enum EE_PERMISSION_TYPE {
  /* Organization Permissions */
  CREATE_PERMISSION_GROUPS = "createPermissionGroups:organization",
  CREATE_USERGROUPS = "createUserGroups:organization",
  ORGANIZATION_MANAGE_ALL_USERS = "organizationManageAllUsers:organization",
  ORGANIZATION_MANAGE_USER_GROUPS = "organizationManageUserGroups:organization",
  ORGANIZATION_READ_USER_GROUPS = "organizationReadUserGroups:organization",
  ORGANIZATION_MANAGE_PERMISSION_GROUPS = "organizationManagePermissionGroups:organization",
  ORGANIZATION_ADD_USER_TO_ALL_USER_GROUPS = "organizationAddUsersToGroups:organization",
  ORGANIZATION_REMOVE_USER_FROM_ALL_USER_GROUPS = "organizationRemoveUserFromGroups:organization",
  ORGANIZATION_READ_PERMISSION_GROUPS = "organizationReadPermissionGroups:organization",
  ORGANIZATION_ASSIGN_PERMISSION_GROUPS = "organizationAssignPermissionGroups:organization",
  ORGANIZATION_DELETE_USER_GROUPS = "organizationDeleteUserGroups:organization",
  ORGANIZATION_DELETE_PERMISSION_GROUPS = "organizationDeletePermissionGroups:organization",
  ORGANIZATION_UNASSIGN_PERMISSION_GROUPS = "organizationUnassignPermissionGroups:organization",
  MANAGE_ORGANIZATION = "manage:organizations",
  READ_AUDIT_LOGS = "readAuditLogs:organization",

  /* Application permissions */
  CREATE_APPLICATION = "create:applications",
  DELETE_APPLICATION = "delete:applications",
  INVITE_USER_TO_APPLICATION = "inviteUsers:applications",

  /* User Permissions */
  MANAGE_USERS = "manage:users",
  DELETE_USERS = "delete:users",

  /* User group permissions i.e Groups */
  DELETE_USERGROUPS = "delete:userGroups",
  MANAGE_USERGROUPS = "manage:userGroups",
  READ_USERGROUPS = "read:userGroups",
  ADD_USERS_TO_USERGROUPS = "addUsers:userGroups",
  REMOVE_USERS_FROM_USERGROUPS = "removeUsers:userGroups",

  /* Permission group permissions i.e Roles */
  ASSIGN_PERMISSIONGROUPS = "assign:permissionGroups",
  DELETE_PERMISSIONGROUPS = "delete:permissionGroups",
  MANAGE_PERMISSIONGROUPS = "manage:permissionGroups",
  READ_PERMISSIONGROUPS = "read:permissionGroups",
  UNASSIGN_PERMISSIONGROUPS = "unassign:permissionGroups",

  /* Workspace Permissions */
  DELETE_WORKSPACE = "delete:workspace",

  /* Environment permissions */
  EXECUTE_ENVIRONMENT = "execute:environments",
  MANAGE_WORKSPACE_ENVIRONMENT = "manage:workspaceEnvironments",
  DELETE_WORKSPACE_ENVIRONMENT = "delete:workspaceEnvironments",
  VIEW_WORKSPACE_ENVIRONMENT = "read:workspaceEnvironments",
  CREATE_ENVIRONMENT = "create:environments",
  DELETE_ENVIRONMENT = "delete:environments",
  MANAGE_ENVIRONMENT = "manage:environments",

  /* Package permissions */
  MANAGE_WORKSPACE_PACKAGES = "manage:workspacePackages",
  CREATE_PACKAGE = "create:packages",
  EXPORT_PACKAGES = "export:packages",
  MANAGE_PACKAGES = "manage:packages",
  DELETE_PACKAGE = "delete:packages",

  /* Module permissions */
  CREATE_MODULES = "create:modules",
  MANAGE_MODULES = "manage:modules",
  DELETE_MODULES = "delete:modules",
  CREATE_MODULES_ACTIONS = "create:moduleActions",

  /* Module instance permissions */
  DELETE_MODULE_INSTANCES = "delete:moduleInstances",
  MANAGE_MODULE_INSTANCES = "manage:moduleInstances",
  EXECUTE_MODULE_INSTANCES = "execute:moduleInstances",

  /* Workflow permissions */
  MANAGE_WORKSPACE_WORKFLOWS = "manage:workspaceWorkflows",
  READ_WORKSPACE_WORKFLOWS = "read:workspaceWorkflows",
  DELETE_WORKSPACE_WORKFLOWS = "delete:workspaceWorkflows",
  PUBLISH_WORKSPACE_WORKFLOWS = "publish:workspaceWorkflows",
  EXPORT_WORKSPACE_WORKFLOW = "export:workspaceWorkflows",
  EXECUTE_WORKSPACE_WORKFLOW = "execute:workflows",
  EXPORT_WORKFLOWS = "export:workflows",
  CREATE_WORKFLOWS = "create:workflows",
  DELETE_WORKFLOWS = "delete:workflows",
  MANAGE_WORKFLOWS = "manage:workflows",
  READ_WORKFLOWS = "read:workflows",
  CREATE_WORKFLOW_ACTIONS = "create:workflowActions",
  /* Git package permissions */
  GIT_PACKAGE_CONNECT = "connectToGit:packages",
  GIT_PACKAGE_MANAGE_PROTECTED_BRANCHES = "manageProtectedBranches:packages",
  GIT_PACKAGE_MANAGE_DEFAULT_BRANCH = "manageDefaultBranches:packages",
  GIT_PACKAGE_MANAGE_AUTO_COMMIT = "manageAutoCommit:packages",
}

export const PERMISSION_TYPE = {
  ...CE_PERMISSION_TYPE,
  ...EE_PERMISSION_TYPE,
};

export const hasDeleteApplicationPermission = (permissions: string[] = []) => {
  return isPermitted(permissions, PERMISSION_TYPE.DELETE_APPLICATION);
};

export const hasCreateNewAppPermission = (permissions: string[] = []) => {
  return isPermitted(permissions, PERMISSION_TYPE.CREATE_APPLICATION);
};

export const hasDeleteWorkspacePermission = (permissions: string[] = []) => {
  return isPermitted(permissions, PERMISSION_TYPE.DELETE_WORKSPACE);
};

export const hasInviteUserToApplicationPermission = (
  permissions: string[] = [],
) => {
  return isPermitted(permissions, PERMISSION_TYPE.INVITE_USER_TO_APPLICATION);
};

export const hasCreateWorkspacePermission = (permissions: string[] = []) => {
  return isPermitted(permissions, PERMISSION_TYPE.CREATE_WORKSPACE);
};

export const hasCreateDatasourcePermission = (permissions: string[] = []) =>
  isPermitted(permissions, PERMISSION_TYPE.CREATE_DATASOURCES);

export const hasReadDatasourcePermission = (permissions: string[] = []) =>
  isPermitted(permissions, PERMISSION_TYPE.READ_DATASOURCES);

export const hasManageDatasourcePermission = (permissions: string[] = []) =>
  isPermitted(permissions, PERMISSION_TYPE.MANAGE_DATASOURCES);

export const hasManageWorkspaceDatasourcePermission = (
  permissions: string[] = [],
) => isPermitted(permissions, PERMISSION_TYPE.MANAGE_WORKSPACE_DATASOURCES);

export const hasDeleteDatasourcePermission = (permissions: string[] = []) =>
  isPermitted(permissions, PERMISSION_TYPE.DELETE_DATASOURCES);

export const hasCreateDatasourceActionPermission = (
  permissions: string[] = [],
) => isPermitted(permissions, PERMISSION_TYPE.CREATE_DATASOURCE_ACTIONS);

export const hasCreatePagePermission = (permissions: string[] = []) =>
  isPermitted(permissions, PERMISSION_TYPE.CREATE_PAGES);

export const hasManagePagePermission = (permissions: string[] = []) =>
  isPermitted(permissions, PERMISSION_TYPE.MANAGE_PAGES);

export const hasDeletePagePermission = (permissions: string[] = []) =>
  isPermitted(permissions, PERMISSION_TYPE.DELETE_PAGES);

export const hasCreateActionPermission = (permissions: string[] = []) =>
  isPermitted(permissions, PERMISSION_TYPE.CREATE_ACTIONS);

export const hasManageActionPermission = (permissions: string[] = []) =>
  isPermitted(permissions, PERMISSION_TYPE.MANAGE_ACTIONS);

export const hasDeleteActionPermission = (permissions: string[] = []) =>
  isPermitted(permissions, PERMISSION_TYPE.DELETE_ACTIONS);

export const hasExecuteActionPermission = (permissions: string[] = []) =>
  isPermitted(permissions, PERMISSION_TYPE.EXECUTE_ACTIONS);

export const hasAuditLogsReadPermission = (permissions: string[] = []) =>
  isPermitted(permissions, PERMISSION_TYPE.READ_AUDIT_LOGS);

// Package permissions start
export const hasManageWorkspacePackagePermission = (
  permissions: string[] = [],
) => isPermitted(permissions, PERMISSION_TYPE.MANAGE_WORKSPACE_PACKAGES);

export const hasCreatePackagePermission = (permissions: string[] = []) =>
  isPermitted(permissions, PERMISSION_TYPE.CREATE_PACKAGE);

export const hasDeletePackagePermission = (permissions: string[] = []) =>
  isPermitted(permissions, PERMISSION_TYPE.DELETE_PACKAGE);

export const hasManagePackagePermission = (permissions: string[] = []) =>
  isPermitted(permissions, PERMISSION_TYPE.MANAGE_PACKAGES);

export const hasExportPackagePermission = (permissions: string[] = []) =>
  isPermitted(permissions, PERMISSION_TYPE.EXPORT_PACKAGES);

// Package permissions end

// Workflow permissions start

export const hasManageWorkspaceWorkflowPermission = (
  permissions: string[] = [],
) => isPermitted(permissions, PERMISSION_TYPE.MANAGE_WORKSPACE_WORKFLOWS);

export const hasCreateWorkflowPermission = (permissions: string[] = []) =>
  isPermitted(permissions, PERMISSION_TYPE.CREATE_WORKFLOWS);

export const hasDeleteWorkflowPermission = (permissions: string[] = []) =>
  isPermitted(permissions, PERMISSION_TYPE.DELETE_WORKFLOWS);

export const hasManageWorkflowPermission = (permissions: string[] = []) =>
  isPermitted(permissions, PERMISSION_TYPE.MANAGE_WORKFLOWS);

export const hasCreateWorkflowActionsPermission = (
  permissions: string[] = [],
) => isPermitted(permissions, PERMISSION_TYPE.CREATE_WORKFLOW_ACTIONS);

// Workflow permissions end

// Module permissions start
export const hasCreateModulePermission = (permissions?: string[]) =>
  isPermitted(permissions, PERMISSION_TYPE.CREATE_MODULES);

export const hasManageModulePermission = (permissions?: string[]) =>
  isPermitted(permissions, PERMISSION_TYPE.MANAGE_MODULES);

export const hasDeleteModulePermission = (permissions?: string[]) =>
  isPermitted(permissions, PERMISSION_TYPE.DELETE_MODULES);

export const hasCreateModuleActionsPermission = (permissions?: string[]) =>
  isPermitted(permissions, PERMISSION_TYPE.CREATE_MODULES_ACTIONS);

export const hasExecuteModuleInstancePermission = (permissions?: string[]) =>
  isPermitted(permissions, PERMISSION_TYPE.EXECUTE_MODULE_INSTANCES);

export const hasCreateModuleDatasourceActionPermission = (
  permissions: string[] = [],
) => isPermitted(permissions, [PERMISSION_TYPE.CREATE_DATASOURCE_ACTIONS]);

// Module permissions end

// Environment permissions start
export const hasManageWorkspaceEnvironmentPermission = (
  permissions?: string[],
) =>
  isPermitted(
    permissions,
    [
      PERMISSION_TYPE.MANAGE_WORKSPACE_ENVIRONMENT,
      PERMISSION_TYPE.CREATE_ENVIRONMENT,
      PERMISSION_TYPE.DELETE_WORKSPACE_ENVIRONMENT,
      PERMISSION_TYPE.VIEW_WORKSPACE_ENVIRONMENT,
    ],
    LOGIC_FILTER.OR,
  );

export const hasManageEnvironmentPermission = (permissions?: string[]) =>
  isPermitted(permissions, PERMISSION_TYPE.MANAGE_ENVIRONMENT);

export const hasCreateEnvironmentPermission = (permissions?: string[]) =>
  isPermitted(permissions, PERMISSION_TYPE.CREATE_ENVIRONMENT);

export const hasDeleteEnvironmentPermission = (permissions?: string[]) =>
  isPermitted(permissions, PERMISSION_TYPE.DELETE_ENVIRONMENT);
// Environment permissions end

export const hasDeleteModuleInstancePermission = (permissions?: string[]) =>
  isPermitted(permissions, PERMISSION_TYPE.DELETE_MODULE_INSTANCES);

export const hasManageModuleInstancePermission = (permissions?: string[]) =>
  isPermitted(permissions, PERMISSION_TYPE.MANAGE_MODULE_INSTANCES);

// git permissions
export const hasGitPkgConnectPermission = (permissions: string[] = []) => {
  return isPermitted(permissions, PERMISSION_TYPE.GIT_PACKAGE_CONNECT);
};

export const hasGitPkgnManageProtectedBranchesPermission = (
  permissions: string[] = [],
) => {
  return isPermitted(
    permissions,
    PERMISSION_TYPE.GIT_PACKAGE_MANAGE_PROTECTED_BRANCHES,
  );
};

export const hasGitPkgManageDefaultBranchPermission = (
  permissions: string[] = [],
) => {
  return isPermitted(
    permissions,
    PERMISSION_TYPE.GIT_PACKAGE_MANAGE_DEFAULT_BRANCH,
  );
};

export const hasGitPkgManageAutoCommitPermission = (
  permissions: string[] = [],
) => {
  return isPermitted(
    permissions,
    PERMISSION_TYPE.GIT_PACKAGE_MANAGE_AUTO_COMMIT,
  );
};
